import { NavLink, useLocation } from 'react-router-dom'
import React, { useRef, useEffect, useCallback } from 'react'
import './OpenSideMenu.scss'
import { motion } from 'framer-motion'
import useOutsideClick from '@hooks/useOutsideClick'
import SpatialNavigation from '@utils/spatial-navigation.js'
import { focusableRoutes, mainRoutes, routes } from '@constants/routes.const'
import { setMenuIcon } from '../menuIcons'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import { MenuItem } from '@interfaces/index'
import { ProfileIcon } from '@components/Profile/ProfileIcon/ProfileIcon'
import { LocalStorageItem, SessionStorageItem } from '@constants/index'
import { VelvetSDK } from 'sdk'
import { KidsTagVariant } from '@enums/profile.enum'

export interface OpenSideMenuProps {
  setOpenMenu: (arg: boolean) => void
  menuItems?: MenuItem[]
}
export const OpenSideMenu = ({ setOpenMenu, menuItems }: OpenSideMenuProps) => {
  const ref = useRef<HTMLAnchorElement>(null)
  const location = useLocation()

  const activeProfileObject = JSON.parse(localStorage.getItem(LocalStorageItem.ACTIVE_PROFILE) as string)
  const activeProfileNameFromLS = activeProfileObject?.name
  const activeProfileEmailFromLS = activeProfileObject?.email
  const activeProfileDisplayName = activeProfileNameFromLS || activeProfileEmailFromLS?.split('@')[0]
  const SN = SpatialNavigation
  const V = VelvetSDK.Vocabulary.vocabulary

  useEffect(() => {
    ref.current ? ref.current.focus() : SN && typeof SN.focus === 'function' && SN.focus()
  }, [SN])

  const closeMenuOnClick = useCallback(
    (pathUrl: string) => {
      setOpenMenu(false)
      if (pathUrl === location.pathname) SN.focus('app-content')

      pathUrl.includes(mainRoutes.HOME) && sessionStorage.removeItem(SessionStorageItem.LAST_FOCUSED_ITEM_HOME)
      pathUrl === routes.MY_LIST && sessionStorage.removeItem(SessionStorageItem.LAST_FOCUSED_ITEM_MY_LIST)
    },
    [setOpenMenu, SN, location],
  )

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false)
    SN.focus('app-content')
  }, [SN, setOpenMenu])

  const menuRef = useOutsideClick<HTMLDivElement>(handleCloseMenu)

  return (
    <div className="open-side-menu-container">
      <div className="open-side-menu" ref={menuRef}>
        <div className="open-side-menu-user">
          <NavLink
            to={routes.MANAGE_PROFILES}
            id="profile"
            data-sn-down={`#${focusableRoutes.MENU}-item-0`}
            data-sn-up=""
            tabIndex={-1}
            onClick={() => closeMenuOnClick(routes.MANAGE_PROFILES)}
            className={({ isActive }) =>
              `${
                isActive && location.pathname === routes.MANAGE_PROFILES ? 'active ' : ''
              }open-side-menu-profile focusable`
            }
            ref={location.pathname === routes.MANAGE_PROFILES ? ref : null}
          >
            <div
              className="open-side-menu-profile-image"
              style={{ backgroundColor: activeProfileObject.image.backgroundColor }}
            >
              <ProfileIcon profileData={activeProfileObject} kidsTagVariant={KidsTagVariant.MENU} />
            </div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
              <div style={{ height: '58px' }}>
                <Text tag={TextTag.SPAN} className="open-side-menu-profile-name">
                  {activeProfileDisplayName}
                </Text>
                <Text tag={TextTag.SPAN} className="open-side-menu-profile-switch">
                  {V.profileSwitch}
                </Text>
              </div>
            </motion.div>
          </NavLink>
        </div>
        <div className="open-side-menu-navigation">
          {menuItems?.map(({ path, icon, text }, idx) => {
            return (
              <NavLink
                to={`${path}`}
                key={`${focusableRoutes.MENU}-item-${idx}`}
                id={`${focusableRoutes.MENU}-item-${idx}`}
                data-sn-down={`#${focusableRoutes.MENU}-item-${idx + 1}`}
                data-sn-up={idx === 0 ? `#${focusableRoutes.PROFILE}` : `#${focusableRoutes.MENU}-item-${idx - 1}`}
                tabIndex={-1}
                className={({ isActive }) => `${isActive && location.pathname === path ? 'active ' : ''}focusable`}
                onClick={() => closeMenuOnClick(path)}
                ref={location.pathname === path ? ref : null}
              >
                <div className="open-side-menu-wrapper">
                  <div className="open-side-menu-item">
                    {setMenuIcon(icon)}
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                      <Text tag={TextTag.P} className="open-side-menu-text">
                        {text}
                      </Text>
                    </motion.div>
                  </div>
                  <div className="side-menu-border"></div>
                </div>
              </NavLink>
            )
          })}
        </div>
      </div>
    </div>
  )
}
